<template>
  <div
    key="battleView"
    class="u-spaceitems"
    :class="[$options.$styles.wrap, { ' pt-10 ': !orderedCombatants.length }]"
    data-test="battleViewWrapper"
    :style="{
      '--animation-speed': animationCancelled ? `0s` : null,
    }"
  >
    <vue-headful v-if="meta && meta.name" :title="`${meta.name} | BattleFX`" />

    <fader animation="quickfade">
      <div class="relative pt-px" v-show="!animationCancelled">
        <h1
          v-if="meta"
          class=" opacity-0 flex font-light justify-center text-accent mb-4 md:mb-6 -mt-8 "
          :class="
            orderedCombatants.length
              ? ` opacity-50 text-sm `
              : ` opacity-100 text-inherit-size `
          "
          style="transition: opacity 1s ease 1s;"
          data-test="pageHeading"
        >
          <slot name="heading">
            {{ meta.name || `unnamed battle` }}
          </slot>
        </h1>
        <div
          v-if="meta && meta.round"
          :class="[
            $options.$styles.counterWrap,
            { ' sm:pt-12 ': ui.toolbarIsTop },
          ]"
          data-test="roundCounterPadding"
        >
          <round-counter
            v-if="orderedCombatants.length"
            @decrement="prevTurn"
            @increment="nextTurn"
            :round="meta.round"
            :currentCombatantName="currentCombatantName"
          />
        </div>
        <fader>
          <button
            :class="$options.$styles.initiative"
            @click.prevent="initiativeHandler"
            aria-label="Roll initiative"
            title="Roll initiative"
            v-if="orderedCombatants.length"
          >
            <icon data-id="reroll" class="fill-current" />
          </button>
        </fader>
        <item-animator
          :active-index="meta.turn"
          :items="orderedCombatants"
          data-test="combatantList"
          v-if="meta"
        >
          <template v-slot="item">
            <combatant :data="item" />
          </template>
        </item-animator>
        <fader>
          <div
            class="border-bg-lightest bg-tint-less border-t relative z-1"
            id="CombatantAdder"
            style="transition-delay: .5s"
          >
            <add-field
              autofocus
              placeholder="Add a combatant…"
              button-title="Add combatant"
              button-classes="absolute t l-full text-accent hover:text-accent py-4"
              field-classes="py-4 px-3 max-md:pl-2"
              @submit.prevent="createItem"
            />
          </div>
        </fader>
      </div>
    </fader>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import AddField from '@/components/AddField'
import Combatant from '@/components/Combatant'
import ItemAnimator from '@/components/ItemAnimator'
import RoundCounter from '@/components/RoundCounter'
import { createItem } from '@/utilities/helpers'

export default {
  name: `Battle`,

  components: {
    AddField,
    Combatant,
    ItemAnimator,
    RoundCounter,
  },

  data: () => ({
    animationCancelled: true,
    ready: false,
  }),

  props: {
    battleId: {
      type: String,
      default: null,
    },
    skipAnimation: {
      type: Boolean,
      default: false,
    },
  },

  inject: [`ui`],

  async created() {
    this.createItem = createItem(this.create)
    this.animationCancelled = this.skipAnimation
    this.setCurrent(this.battleId)
    this.bindMetaRef(this.battleId)
    await this.bindCombatantsRef(this.battleId)
    setTimeout(() => {
      this.animationCancelled = false
    }, 100)
  },

  beforeRouteEnter(to, from, next) {
    to.params.skipAnimation = from.name !== `battles`
    next()
  },

  computed: {
    ...mapState(`battle`, [`meta`]),
    ...mapState(`battle/turns`, {
      currentCombatantName: `name`,
    }),
    ...mapGetters(`battle`, [`orderedCombatants`]),
    ...mapGetters(`battle/rounds`, [`round`]),
    ...mapGetters(`battle/turns`, [`turn`]),
  },

  methods: {
    ...mapActions(`battles`, [`setCurrent`]),
    ...mapActions(`battle`, [
      `bindMetaRef`,
      `bindCombatantsRef`,
      `beginRollInitiative`,
    ]),
    ...mapActions(`battle/rounds`, {
      resetRound: `reset`,
    }),
    ...mapActions(`battle/turns`, {
      resetTurn: `reset`,
      nextTurn: `next`,
      prevTurn: `prev`,
    }),
    ...mapActions(`combatant`, [`create`]),

    initiativeHandler() {
      if (this.round > 1 && this.initiativeCancelled()) return false
      this.beginRollInitiative()
      this.resetTurn()
      this.resetRound()
    },

    initiativeCancelled() {
      return !confirm(
        `Rolling initiative will set the round back to “1”. Continue?`
      )
    },
  },

  $styles: {
    counterWrap: `
      fixed gradient-to-bottom l pointer-events-none t w-full z-1
    `,
    initiative: `
      -mr-2 md:-mb-1 lg:-mb-0 absolute active:opacity-100 b-full cursor-default flex hover:opacity-60 hover:text-accent md:-mr-px opacity-40 p-3 r-full rounded-full text-accent text-xl
    `,
    wrap: `
      flex flex-1 flex-col justify-center pb-10 max-md:pt-38 md:pt-40 lg:pt-44
    `,
  },
}
</script>

<style lang="postcss" scoped>
.gradient-to-bottom {
  background: linear-gradient(var(--bg-color) 50%, var(--bg-full-trans-color)),
    linear-gradient(var(--bg-color) 50%, var(--bg-full-trans-color));
  background-repeat: no-repeat;
  background-position: 0 -2rem;
}
</style>
